import { Fragment, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { STARS } from '../../../../constants/grade';
import { SVGIconType } from '../../../../constants/svgIcon';
import { AlbumPartition, Performances } from '../../../../models/SpaceStore';
import { Profile, WebBoosterType } from '../../../../models/UserStore';
import { getDateString } from '../../../../utils/date';
import {
  AppBar,
  AvatarNode,
  FolderBreadcrumbRow,
  FolderBreadcrumbVariant,
  LoadingSpinner,
  SvgIcon,
} from '../../../Molecules';
import { SummaryType } from '../../../Popups/ReportSummaryPopup';

import styles from './PerformanceReportTemplateV2.module.css';

type PerformanceSummary = {
  [SummaryType.GamePlayed]: number;
  [SummaryType.GameScore]: number;
  [SummaryType.TimePlayed]: string;
};

export type PerformanceReportTemplateV2Props = {
  userProfile: Profile | undefined | null;
  lastModifiedAt: Date | undefined;
  selectedPeriod: string;
  periodRange: string;
  webBoosterType: WebBoosterType | undefined;
  breadcrumbs: AlbumPartition[];
  summary: PerformanceSummary;
  analysis: Performances;
  isLoading: boolean;
  onClickPeriod: () => void;
  onClickBreadcrumb: () => void;
  onClickSummary: (summaryType: SummaryType) => void;
  onClickAnalysisLevel: (analysisLevel: number) => void;
};

export const PerformanceReportTemplateV2 = memo(
  ({
    userProfile,
    lastModifiedAt,
    selectedPeriod,
    // periodRange,
    webBoosterType,
    breadcrumbs,
    summary,
    analysis,
    isLoading,
    onClickPeriod,
    onClickBreadcrumb,
    onClickSummary,
    onClickAnalysisLevel,
  }: PerformanceReportTemplateV2Props) => {
    const { t, i18n } = useTranslation();

    return (
      <div className={styles.container}>
        <AppBar title={t('Performance Report')} withBack={true} />
        <div className={styles.centeredContainer}>
          <div className={styles.titleContainer}>
            {userProfile ? (
              <AvatarNode profile={userProfile} size={60} />
            ) : null}
            <div className={styles.titleTextContainer}>
              <div className={styles.titleTop}>{userProfile?.name}</div>
              <div className={styles.subtitle}>
                {t('Last Modified: ')}
                {getDateString({
                  date: lastModifiedAt,
                  lang: i18n.language,
                  remain: ' HH:mm',
                  replace: true,
                })}
              </div>
            </div>
          </div>

          <div className={styles.folderBreadcrumbRow}>
            <div>{t('Path: ')}</div>
            <FolderBreadcrumbRow
              webBoosterType={webBoosterType}
              breadcrumbs={breadcrumbs}
              variant={FolderBreadcrumbVariant.Black}
              onClickBreadcrumb={onClickBreadcrumb}
            />
          </div>

          <div className={styles.periodSection} onClick={onClickPeriod}>
            <div className={styles.periodText}>
              <div className={styles.periodTop}>
                <span className={styles.periodTitle}>{t('Period: ')}</span>
                <SvgIcon
                  name={SVGIconType.Period}
                  className={styles.periodIcon}
                />
                <span className={styles.selectedPeriod}>
                  {t(selectedPeriod)}
                </span>
              </div>
              {/* <span className={styles.periodDate}>{periodRange}</span> */}
            </div>
            <SvgIcon
              name={SVGIconType.Triangle}
              className={styles.triangleIcon}
            />
          </div>

          {!summary[SummaryType.GamePlayed] ? (
            <div className={styles.noLearningRecord}>
              {t('No learning records found for your selected period.')}
            </div>
          ) : null}

          <div className={styles.summaryBigTitle}>{t('Summary')}</div>

          <div className={styles.statsGrid}>
            <div className={styles.statsGridLeftPart}>
              <div
                className={styles.stat}
                onClick={() => {
                  onClickSummary(SummaryType.GamePlayed);
                }}
              >
                <div className={styles.statTitleContainer}>
                  <SvgIcon
                    name={SVGIconType.PracticeTick}
                    className={styles.iconTitle}
                  />
                  <div className={styles.summaryTitle}>
                    {t('Practice Completed')}
                  </div>
                </div>
                <div className={styles.statText}>
                  {summary[SummaryType.GamePlayed]}
                </div>
              </div>
              <div
                className={styles.stat}
                onClick={() => {
                  onClickSummary(SummaryType.TimePlayed);
                }}
              >
                <div className={styles.statTitleContainer}>
                  <SvgIcon
                    name={SVGIconType.DoubleClock}
                    className={styles.iconTitle}
                  />
                  <div className={styles.summaryTitle}>{t('Time Spent')}</div>
                </div>
                <div className={styles.statText}>
                  {summary[SummaryType.TimePlayed]}
                </div>
              </div>
            </div>
            <div
              className={styles.stat}
              onClick={() => {
                onClickSummary(SummaryType.GameScore);
              }}
            >
              <div className={styles.statTitleContainer}>
                <SvgIcon
                  name={SVGIconType.DoubleScore}
                  className={styles.iconTitle}
                />
                <div className={styles.summaryTitle}>{t('Average Score')}</div>
              </div>
              <div className={styles.statText}>
                {summary[SummaryType.GameScore]}
                <SvgIcon
                  name={SVGIconType.DirectArrow}
                  width={28}
                  height={28}
                  color={'#00a698'}
                  className={styles.directArrowRotated}
                />
              </div>
            </div>
          </div>

          <div className={styles.analysisTitle}>
            <div className={styles.analysisBigTitle}>{t('Analysis')}</div>
            {/* <div className={styles.overall}>{t('Overall')}</div> */}
          </div>

          <div className={styles.levelList}>
            {STARS.map((item, index) => {
              const itemCount = analysis[item.stars] ?? 0;
              return (
                <Fragment key={item.stars}>
                  <div
                    className={`${styles.analysisItemContainer} ${
                      itemCount === 0 ? styles.disabled : ''
                    }`}
                    onClick={
                      itemCount > 0
                        ? () => {
                            onClickAnalysisLevel(item.stars);
                          }
                        : undefined
                    }
                  >
                    <SvgIcon
                      name={SVGIconType.ScoreAnalysis}
                      className={styles.analysisIcon}
                    />
                    <div className={styles.analysisTextContainer}>
                      <span className={styles.analysisLevelKey}>
                        {t(`${item.text}`)}
                      </span>
                      <div className={styles.analysisScoreContainer}>
                        <SvgIcon
                          name={SVGIconType.Score}
                          className={styles.analysisScoreIcon}
                        />
                        <span className={styles.analysisScoreRange}>
                          {`${item.min}-${item.max}`}
                        </span>
                      </div>
                    </div>
                    <div className={styles.analysisRightPart}>
                      <span className={styles.analysisLevelCount}>
                        {itemCount}
                      </span>
                      {itemCount > 0 && (
                        <SvgIcon
                          name={SVGIconType.Arrow}
                          className={styles.arrowIcon}
                        />
                      )}
                    </div>
                  </div>
                  {index !== STARS.length - 1 && (
                    <hr className={styles.divider} />
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
        {isLoading && <LoadingSpinner loading={isLoading} />}
      </div>
    );
  },
);

export default PerformanceReportTemplateV2;
